import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import SystemResourceGraphs from '../components/SystemResourceGraphs';
import { getApiBaseUrl } from '../config/apiBase';
import tokenManager from '../utils/tokenManager';

// PROMPT-133: Host-Based API Calls (No localhost)
const API_BASE_URL = getApiBaseUrl();

console.log('[DevMonitor] Initialized with base URL:', API_BASE_URL);

const DevMonitor = () => {
  const [mainServices, setMainServices] = useState([]);
  const [microServices, setMicroServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(10);
  const [actionLoading, setActionLoading] = useState({});
  const [systemInfo, setSystemInfo] = useState(null);

  const fetchServiceStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/dev/service-status');
      
      if (response.data) {
        // Set main services and microservices separately
        if (response.data.main_services) {
          setMainServices(response.data.main_services);
        }
        if (response.data.microservices) {
          setMicroServices(response.data.microservices);
        }
        setLastRefresh(new Date());
      }
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setError('Dev monitoring is disabled. Set dev_monitoring.enabled=true in config to enable.');
      } else {
        setError(`Failed to fetch service status: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSystemInfo = useCallback(async () => {
    try {
      const response = await api.get('/dev/system-info');
      setSystemInfo(response.data);
    } catch (err) {
      console.error('Failed to fetch system info:', err);
    }
  }, []);

  useEffect(() => {
    fetchServiceStatus();
    fetchSystemInfo();
  }, [fetchServiceStatus, fetchSystemInfo]);

  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      const interval = setInterval(() => {
        fetchServiceStatus();
      }, refreshInterval * 1000);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, fetchServiceStatus]);

  const handleServiceControl = async (serviceName, action) => {
    const key = `${serviceName}-${action}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));

    try {
      const response = await api.post('/dev/service-control', {
        service: serviceName,
        action: action
      });

      if (response.data.success) {
        setTimeout(() => {
          fetchServiceStatus();
        }, 2000);
      } else {
        alert(`Failed: ${response.data.message}`);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      alert(`Error: ${errorMsg}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'stopped':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'unhealthy':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return '●';
      case 'stopped':
        return '○';
      case 'unhealthy':
        return '◐';
      default:
        return '?';
    }
  };

  const groupedServices = mainServices.reduce((acc, service) => {
    if (!acc[service.type]) {
      acc[service.type] = [];
    }
    acc[service.type].push(service);
    return acc;
  }, {});

  const groupedMicroServices = microServices.reduce((acc, service) => {
    if (!acc[service.type]) {
      acc[service.type] = [];
    }
    acc[service.type].push(service);
    return acc;
  }, {});

  // Sort services within each group by status (stopped first, running last)
  Object.keys(groupedServices).forEach(type => {
    groupedServices[type].sort((a, b) => {
      // Define status priority: stopped (0), unhealthy (1), unknown (2), running (3)
      const statusPriority = {
        'stopped': 0,
        'unhealthy': 1,
        'unknown': 2,
        'running': 3
      };
      
      const aPriority = statusPriority[a.status] || 2;
      const bPriority = statusPriority[b.status] || 2;
      
      // If status priority is the same, sort by last_checked (newest first)
      if (aPriority === bPriority) {
        return new Date(b.last_checked) - new Date(a.last_checked);
      }
      
      return aPriority - bPriority;
    });
  });

  // Sort microservices within each group by status
  Object.keys(groupedMicroServices).forEach(type => {
    groupedMicroServices[type].sort((a, b) => {
      // Define status priority: stopped (0), unhealthy (1), unknown (2), running (3)
      const statusPriority = {
        'stopped': 0,
        'unhealthy': 1,
        'unknown': 2,
        'running': 3
      };
      
      const aPriority = statusPriority[a.status] || 2;
      const bPriority = statusPriority[b.status] || 2;
      
      // If status priority is the same, sort by last_checked (newest first)
      if (aPriority === bPriority) {
        return new Date(b.last_checked) - new Date(a.last_checked);
      }
      
      return aPriority - bPriority;
    });
  });

  if (error && error.includes('disabled')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full border-l-4 border-red-500">
          <div className="flex items-center mb-4">
            <svg className="w-8 h-8 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h1 className="text-2xl font-bold text-gray-800">Dev Monitoring Disabled</h1>
          </div>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="bg-gray-50 p-4 rounded border border-gray-200">
            <p className="text-sm text-gray-700 font-mono">
              Edit: <strong>backend/config/config.yaml</strong>
            </p>
            <pre className="mt-2 text-xs bg-gray-800 text-green-400 p-3 rounded">
{`dev_monitoring:
  enabled: true  # Enable for dev/staging only`}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                <span className="mr-3">🔧</span>
                Dev System Monitor
              </h1>
              <p className="text-sm text-red-600 font-semibold mt-1">
                ⚠️ DEVELOPMENT ONLY - DO NOT USE IN PRODUCTION
              </p>
            </div>
            <div className="text-right">
              {systemInfo && (
                <div className="text-sm text-gray-600">
                  <p><strong>Host:</strong> {systemInfo.hostname}</p>
                  <p><strong>Uptime:</strong> {systemInfo.uptime}</p>
                </div>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between border-t pt-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={fetchServiceStatus}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
              >
                <svg className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh Now
              </button>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm text-gray-700">Auto-refresh</span>
              </label>

              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                disabled={!autoRefresh}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100"
              >
                <option value={5}>5s</option>
                <option value={10}>10s</option>
                <option value={15}>15s</option>
                <option value={30}>30s</option>
                <option value={60}>60s</option>
              </select>
            </div>

            {lastRefresh && (
              <div className="text-sm text-gray-600">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && !error.includes('disabled') && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Services Grid */}
        {loading && mainServices.length === 0 && microServices.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Main Services */}
            {Object.keys(groupedServices).sort().map((type) => (
              <div key={type} className="mb-6">
                <h2 className="text-xl font-bold text-gray-700 mb-3 capitalize flex items-center">
                  {type === 'infrastructure' ? '🏗️' : '⚙️'} {type} Services
                </h2>
                
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Host</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Port</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Checked</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {groupedServices[type].map((service) => (
                        <tr key={service.name} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{service.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600">{service.host}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600">{service.port}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusColor(service.status)}`}>
                              <span className="mr-1">{getStatusIcon(service.status)}</span>
                              {service.status}
                            </span>
                            {service.message && (
                              <div className="text-xs text-gray-500 mt-1">{service.message}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {new Date(service.last_checked).toLocaleTimeString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {service.manageable ? (
                              <div className="flex items-center space-x-1.5">
                                {service.status !== 'running' && (
                                  <button
                                    onClick={() => handleServiceControl(service.name, 'start')}
                                    disabled={actionLoading[`${service.name}-start`]}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-xs font-medium transition-colors"
                                    title="Start service"
                                  >
                                    {actionLoading[`${service.name}-start`] ? (
                                      <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                                    ) : (
                                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M6.3 2.84A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.27l9.344-5.891a1.5 1.5 0 000-2.538L6.3 2.84z"/></svg>
                                    )}
                                    Start
                                  </button>
                                )}
                                {(service.status === 'running' || service.status === 'unhealthy') && (
                                  <>
                                    <button
                                      onClick={() => handleServiceControl(service.name, 'stop')}
                                      disabled={actionLoading[`${service.name}-stop`]}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-xs font-medium transition-colors"
                                      title="Stop service"
                                    >
                                      {actionLoading[`${service.name}-stop`] ? (
                                        <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                                      ) : (
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><rect x="4" y="4" width="12" height="12" rx="1"/></svg>
                                      )}
                                      Stop
                                    </button>
                                    <button
                                      onClick={() => handleServiceControl(service.name, 'restart')}
                                      disabled={actionLoading[`${service.name}-restart`]}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-xs font-medium transition-colors"
                                      title="Restart service"
                                    >
                                      {actionLoading[`${service.name}-restart`] ? (
                                        <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                                      ) : (
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                                      )}
                                      Restart
                                    </button>
                                  </>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 italic">Not manageable</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}

            {/* Microservices */}
            {Object.keys(groupedMicroServices).sort().map((type) => (
              <div key={type} className="mb-6">
                <h2 className="text-xl font-bold text-gray-700 mb-3 capitalize flex items-center">
                  🎯 Microservices ({type})
                </h2>
                
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Host</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Port</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Checked</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {groupedMicroServices[type].map((service) => (
                        <tr key={service.name} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{service.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600">{service.host}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600">{service.port}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              service.status === 'running' ? 'bg-green-100 text-green-800' :
                              service.status === 'stopped' ? 'bg-red-100 text-red-800' :
                              service.status === 'unhealthy' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {getStatusIcon(service.status)} {service.status}
                            </span>
                            {service.message && (
                              <div className="text-xs text-gray-500 mt-1">{service.message}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {new Date(service.last_checked).toLocaleTimeString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              {service.status === 'stopped' && (
                                <button
                                  onClick={() => handleServiceControl(service.name, 'start')}
                                  disabled={actionLoading[`${service.name}-start`]}
                                  className="text-green-600 hover:text-green-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                                  title="Start"
                                >
                                  {actionLoading[`${service.name}-start`] ? (
                                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  )}
                                </button>
                              )}
                              {(service.status === 'running' || service.status === 'unhealthy') && (
                                <>
                                  <button
                                    onClick={() => handleServiceControl(service.name, 'stop')}
                                    disabled={actionLoading[`${service.name}-stop`]}
                                    className="text-red-600 hover:text-red-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                                    title="Stop"
                                  >
                                    {actionLoading[`${service.name}-stop`] ? (
                                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                    ) : (
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                                      </svg>
                                    )}
                                  </button>
                                  <button
                                    onClick={() => handleServiceControl(service.name, 'restart')}
                                    disabled={actionLoading[`${service.name}-restart`]}
                                    className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                                    title="Restart"
                                  >
                                    {actionLoading[`${service.name}-restart`] ? (
                                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                    ) : (
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                      </svg>
                                    )}
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
          </>
        )}

        {/* System Resource Graphs */}
        <SystemResourceGraphs apiBaseUrl={API_BASE_URL} />

        {/* Footer Warning */}
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded mt-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Security Warning:</strong> This page has NO authentication and can control system services. 
                It must ONLY be enabled in development/staging environments. Ensure <code className="bg-yellow-100 px-1 rounded">dev_monitoring.enabled=false</code> in production.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevMonitor;
